import { useTranslate } from "ra-core";

import {
  buildCatalogueOptions,
  buildProjectOptions,
  MAX_ARRAY_OPTIONS,
  MAX_OPTIONS_MESSAGE,
  PROFILE_BEDROOM_LABELS,
  PROFILE_BEDROOM_OPTIONS,
  PROFILE_INTEREST_LEVEL_LABELS,
  PROFILE_INTEREST_LEVELS,
  PROFILE_PROPERTY_TYPE_LABELS,
  PROFILE_PROPERTY_TYPES,
} from "./profileRules";
import {
  ProfileChipGroup,
  type ProfileChipGroupOption,
} from "./ProfileChipGroup";
import { ProfileZoneField } from "./ProfileTextFields";
import type { DealProfile, Deal, ProfileOption, Project } from "../types";

export interface DealProfileGroupsCatalogues {
  buyerTypes: ProfileOption[];
  creditTypes: ProfileOption[];
  timelines: ProfileOption[];
  motivators: ProfileOption[];
  objections: ProfileOption[];
  projects: Project[];
}

export interface DealProfileGroupProps {
  deal: Deal;
  profile: Partial<DealProfile>;
  catalogues: DealProfileGroupsCatalogues;
  canWrite: boolean;
  saveField: (patch: Partial<DealProfile>) => Promise<void>;
}

const NO_DATA_LABEL = "Sin dato";

function buildFixedOptions<T extends string | number>(
  values: readonly T[],
  labels: Record<T, string>,
): ProfileChipGroupOption[] {
  return values.map((value) => ({ id: String(value), label: labels[value] }));
}

const SELLS_OTHER_PROPERTY_OPTIONS: ProfileChipGroupOption[] = [
  { id: "si", label: "Sí" },
  { id: "no", label: "No" },
];

/**
 * "Quién es": tipo de comprador, quién decide (read-only, from POSI, never
 * duplicated) and vende otra propiedad.
 */
export function WhoGroup({
  deal,
  profile,
  catalogues,
  canWrite,
  saveField,
}: DealProfileGroupProps) {
  const translate = useTranslate();
  const readOnly = !canWrite;

  return (
    <div className="flex flex-col gap-4">
      <h3 className="text-sm font-semibold">
        {translate("resources.deals.deal_profile.groups.who", {
          _: "Quién es",
        })}
      </h3>
      <ProfileChipGroup
        label={translate("resources.deals.deal_profile.fields.buyer_type", {
          _: "Tipo de comprador",
        })}
        mode="single"
        options={buildCatalogueOptions(
          catalogues.buyerTypes,
          profile.buyer_type_id != null ? [profile.buyer_type_id] : [],
        )}
        value={profile.buyer_type_id != null ? [profile.buyer_type_id] : []}
        onCommit={(next) => saveField({ buyer_type_id: next[0] ?? null })}
        readOnly={readOnly}
        emptyReadOnlyLabel={NO_DATA_LABEL}
      />
      <div className="flex flex-col gap-1">
        <span className="text-sm font-medium">
          {translate("resources.deals.deal_profile.fields.decision_maker", {
            _: "Quién decide",
          })}
        </span>
        <p className="text-[15px]">
          {deal.posi_decision_maker?.trim()
            ? deal.posi_decision_maker
            : translate(
                "resources.deals.deal_profile.fields.decision_maker_empty",
                { _: "Sin respuesta" },
              )}
        </p>
        <span className="text-xs text-muted-foreground">
          {translate(
            "resources.deals.deal_profile.fields.decision_maker_hint",
            { _: "Se captura en POSI" },
          )}
        </span>
      </div>
      <ProfileChipGroup
        label={translate(
          "resources.deals.deal_profile.fields.sells_other_property",
          { _: "Vende otra propiedad" },
        )}
        mode="single"
        options={SELLS_OTHER_PROPERTY_OPTIONS}
        value={
          profile.sells_other_property == null
            ? []
            : [profile.sells_other_property ? "si" : "no"]
        }
        onCommit={(next) =>
          saveField({
            sells_other_property: next[0] == null ? null : next[0] === "si",
          })
        }
        readOnly={readOnly}
        emptyReadOnlyLabel={NO_DATA_LABEL}
      />
    </div>
  );
}

/** "Qué busca": tipo de propiedad, recámaras, zona, proyectos que compara. */
export function LookingForGroup({
  profile,
  catalogues,
  canWrite,
  saveField,
}: DealProfileGroupProps) {
  const translate = useTranslate();
  const readOnly = !canWrite;

  return (
    <div className="flex flex-col gap-4">
      <h3 className="text-sm font-semibold">
        {translate("resources.deals.deal_profile.groups.looking_for", {
          _: "Qué busca",
        })}
      </h3>
      <ProfileChipGroup
        label={translate("resources.deals.deal_profile.fields.property_type", {
          _: "Tipo de propiedad",
        })}
        mode="single"
        options={buildFixedOptions(
          PROFILE_PROPERTY_TYPES,
          PROFILE_PROPERTY_TYPE_LABELS,
        )}
        value={profile.property_type != null ? [profile.property_type] : []}
        onCommit={(next) =>
          saveField({
            property_type: (next[0] as DealProfile["property_type"]) ?? null,
          })
        }
        readOnly={readOnly}
        emptyReadOnlyLabel={NO_DATA_LABEL}
      />
      <ProfileChipGroup
        label={translate("resources.deals.deal_profile.fields.bedrooms", {
          _: "Recámaras",
        })}
        mode="single"
        options={buildFixedOptions(
          PROFILE_BEDROOM_OPTIONS,
          PROFILE_BEDROOM_LABELS,
        )}
        value={profile.bedrooms != null ? [String(profile.bedrooms)] : []}
        onCommit={(next) =>
          saveField({
            bedrooms: next[0] == null ? null : Number(next[0]),
          })
        }
        readOnly={readOnly}
        emptyReadOnlyLabel={NO_DATA_LABEL}
      />
      <ProfileZoneField
        label={translate("resources.deals.deal_profile.fields.zone", {
          _: "Zona",
        })}
        value={profile.zone ?? null}
        readOnly={readOnly}
        onSave={(value) => saveField({ zone: value })}
      />
      <ProfileChipGroup
        label={translate(
          "resources.deals.deal_profile.fields.compared_projects",
          { _: "Proyectos que compara" },
        )}
        mode="multi"
        max={MAX_ARRAY_OPTIONS}
        maxHint={MAX_OPTIONS_MESSAGE}
        options={buildProjectOptions(
          catalogues.projects,
          profile.compared_project_ids ?? [],
        )}
        value={profile.compared_project_ids ?? []}
        onCommit={(next) => saveField({ compared_project_ids: next })}
        readOnly={readOnly}
        emptyReadOnlyLabel={NO_DATA_LABEL}
      />
    </div>
  );
}

/** "Cómo paga": crédito (the amount fields render alongside it, in the section). */
export function CreditTypeGroup({
  profile,
  catalogues,
  canWrite,
  saveField,
}: DealProfileGroupProps) {
  const translate = useTranslate();
  return (
    <ProfileChipGroup
      label={translate("resources.deals.deal_profile.fields.credit_type", {
        _: "Crédito",
      })}
      mode="single"
      options={buildCatalogueOptions(
        catalogues.creditTypes,
        profile.credit_type_id != null ? [profile.credit_type_id] : [],
      )}
      value={profile.credit_type_id != null ? [profile.credit_type_id] : []}
      onCommit={(next) => saveField({ credit_type_id: next[0] ?? null })}
      readOnly={!canWrite}
      emptyReadOnlyLabel={NO_DATA_LABEL}
    />
  );
}

/** "Cuándo y por qué": plazo, interés, motivadores, objeciones. */
export function TimingGroup({
  profile,
  catalogues,
  canWrite,
  saveField,
}: DealProfileGroupProps) {
  const translate = useTranslate();
  const readOnly = !canWrite;

  return (
    <div className="flex flex-col gap-4">
      <h3 className="text-sm font-semibold">
        {translate("resources.deals.deal_profile.groups.timing", {
          _: "Cuándo y por qué",
        })}
      </h3>
      <ProfileChipGroup
        label={translate("resources.deals.deal_profile.fields.timeline", {
          _: "Plazo",
        })}
        mode="single"
        options={buildCatalogueOptions(
          catalogues.timelines,
          profile.timeline_id != null ? [profile.timeline_id] : [],
        )}
        value={profile.timeline_id != null ? [profile.timeline_id] : []}
        onCommit={(next) => saveField({ timeline_id: next[0] ?? null })}
        readOnly={readOnly}
        emptyReadOnlyLabel={NO_DATA_LABEL}
      />
      {/*
        Almikia design-system.md section 8: Interés stays the same neutral
        chip style as every other dimension -- never a red/green/temperature
        color, even though "Alto/Medio/Bajo" might suggest one.
      */}
      <ProfileChipGroup
        label={translate("resources.deals.deal_profile.fields.interest_level", {
          _: "Interés",
        })}
        mode="single"
        options={buildFixedOptions(
          PROFILE_INTEREST_LEVELS,
          PROFILE_INTEREST_LEVEL_LABELS,
        )}
        value={profile.interest_level != null ? [profile.interest_level] : []}
        onCommit={(next) =>
          saveField({
            interest_level: (next[0] as DealProfile["interest_level"]) ?? null,
          })
        }
        readOnly={readOnly}
        emptyReadOnlyLabel={NO_DATA_LABEL}
      />
      <ProfileChipGroup
        label={translate("resources.deals.deal_profile.fields.motivators", {
          _: "Motivadores",
        })}
        mode="multi"
        max={MAX_ARRAY_OPTIONS}
        maxHint={MAX_OPTIONS_MESSAGE}
        options={buildCatalogueOptions(
          catalogues.motivators,
          profile.motivator_ids ?? [],
        )}
        value={profile.motivator_ids ?? []}
        onCommit={(next) => saveField({ motivator_ids: next })}
        readOnly={readOnly}
        emptyReadOnlyLabel={NO_DATA_LABEL}
      />
      <ProfileChipGroup
        label={translate("resources.deals.deal_profile.fields.objections", {
          _: "Objeciones",
        })}
        mode="multi"
        max={MAX_ARRAY_OPTIONS}
        maxHint={MAX_OPTIONS_MESSAGE}
        options={buildCatalogueOptions(
          catalogues.objections,
          profile.objection_ids ?? [],
        )}
        value={profile.objection_ids ?? []}
        onCommit={(next) => saveField({ objection_ids: next })}
        readOnly={readOnly}
        emptyReadOnlyLabel={NO_DATA_LABEL}
      />
    </div>
  );
}
